import React, { useEffect, useState } from 'react';
import MuiTextField from '@material-ui/core/TextField';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import { FieldValidation } from '@rjsf/utils';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/frontend-plugin-api';
import { AWS_ACCOUNT_RESOURCE_TYPE } from '@eng-portal/czi-extensions-common';

/**
 * A reusable picker that populates its dropdown from a spec field on AWS
 * account catalog entities. Configure via ui:options.specField (e.g. "team",
 * "project", "company"). freeSolo allows typing a new value.
 */
export function CatalogTagPicker({
  onChange,
  rawErrors,
  required,
  formData,
  schema: { title, description },
  uiSchema,
}: FieldExtensionComponentProps<string>): JSX.Element {
  const catalogApi = useApi(catalogApiRef);
  const specField = (uiSchema?.['ui:options']?.specField as string) ?? 'team';
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      try {
        const result = await catalogApi.getEntities({
          filter: { kind: 'Resource', 'spec.type': AWS_ACCOUNT_RESOURCE_TYPE },
        });
        const unique = [
          ...new Set(
            result.items
              .map(e => (e.spec as any)?.[specField] as string | undefined)
              .filter((t): t is string => !!t && t !== 'unknown'),
          ),
        ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        if (!cancelled) setOptions(unique);
      } catch {
        // Catalog unavailable — picker falls back to manual entry
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return (): void => { cancelled = true; };
  }, [catalogApi, specField]);

  return (
    <MuiAutocomplete
      freeSolo
      autoSelect
      value={formData ?? ''}
      options={options}
      loading={loading}
      getOptionLabel={option => option}
      onChange={(_event, value) => onChange((value as string) ?? '')}
      renderInput={params => (
        <MuiTextField
          {...params}
          label={title}
          helperText={description ?? 'Select an existing value or type a new one'}
          margin="dense"
          variant="outlined"
          required={required}
          error={!!rawErrors?.length}
        />
      )}
    />
  );
}

export const CatalogTagPickerSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      specField: {
        type: 'string',
        title: 'The spec field on AWS account entities to extract values from',
      },
    },
  },
  returnValue: {
    type: 'string',
  },
};

export const CatalogTagPickerValidation = (
  formData: string,
  validation: FieldValidation,
): void => {
  if (!formData || formData.trim() === '') {
    validation.addError('This field is required.');
  }
};
